/* eslint-disable react/sort-comp */
import { Component, type ReactNode, type ErrorInfo } from 'react';

import Retry from 'ms-components/Retry';
import { Logger } from 'ms-utils/app-logging';

type FunctionChildren = (props: {
  fetchKey: string;
  retry: () => void;
}) => ReactNode;

type FunctionFallback = (props: {
  error: Error;
  retry: () => void;
}) => ReactNode;

type Props = (
  | {
      children: ReactNode | FunctionChildren;
      fallback: Exclude<ReactNode, undefined> | FunctionFallback;
    }
  | {
      // We make sure when fallback is not provided, children is a function.
      // So this should remind us the fact that
      // we need to pass the fetchKey to the inner component
      // for the retry functionality in the default fallback to work.
      children: FunctionChildren;
      fallback?: never;
    }
) & {
  // The name of the error boundary must be *globally* unique.
  // The name is the unique identifier for the error boundary,
  // and is used to keep a monotonic retry serial number that
  // survives across component remounts.
  //
  // This name will also be shown in Sentry logs when this error
  // boundary catches exceptions
  name: string;
};

type State = {
  error: Error | null;
  retrySerial: number;
};

// boundaryId → lastRetry serial#
const retryRegistry = new Map<string, number>();

function currentSerial(id: string) {
  return retryRegistry.get(id) ?? 0;
}

function bumpSerial(id: string) {
  const next = (retryRegistry.get(id) ?? 0) + 1;
  retryRegistry.set(id, next);
  return next;
}

// boundaryId → count of mounted instances
const mountedInstancesRegistry = new Map<string, number>();

function incrementMountedCount(id: string) {
  const currentCount = mountedInstancesRegistry.get(id) ?? 0;
  const newCount = currentCount + 1;
  mountedInstancesRegistry.set(id, newCount);

  // Whenever we transition from 1 -> 2 mounted instances, report to Sentry.
  // We don't do it for 2 -> 3, 3 -> 4, etc. as that'll be useless noisy.
  //
  // This transition will generally mean one of two things are going on:
  //
  // 1. A developer has not used a globally unique name as required, causing
  //    a name collision between conceptually distinct error boundaries.
  //
  // 2. A deverloper has used some sort of Relay antipattern where they are
  //    iterating over a list and doing separate API calls for each item
  //    in the list, rather than fetching the data in a single query.
  if (newCount === 2) {
    Logger.error(
      `🚨 Multiple instances of ErrorBoundaryWithRetry with id "${id}" detected.`,
    );
  }

  return newCount;
}

function decrementMountedCount(id: string) {
  const currentCount = mountedInstancesRegistry.get(id) ?? 0;
  const newCount = Math.max(0, currentCount - 1);

  if (newCount === 0) {
    mountedInstancesRegistry.delete(id);
  } else {
    mountedInstancesRegistry.set(id, newCount);
  }

  return newCount;
}

/**
 * Important: For retry functionality to work, ensure that the inner component
 * receives the fetchKey. This allows the component to re-fetch or re-render
 * based on the updated fetchKey value.
 */
export default class ErrorBoundaryWithRetry extends Component<Props, State> {
  private readonly id = this.props.name;

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override state: State = {
    error: null,
    retrySerial: currentSerial(this.id),
  };

  override componentDidMount() {
    incrementMountedCount(this.id);
  }

  override componentWillUnmount() {
    decrementMountedCount(this.id);
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    Logger.error(
      `ErrorBoundary (${this.props.name}) caught error: ${error.message}`,
      {
        extra: {
          componentStack: info.componentStack,
          errorBoundaryName: this.props.name,
        },
      },
    );
  }

  _retry = () => {
    const retrySerial = bumpSerial(this.id);
    this.setState({ error: null, retrySerial });
  };

  reset() {
    this.setState(prevState => {
      if (prevState.error === null) return null; // no re-render needed
      return { error: null, retrySerial: bumpSerial(this.id) };
    });
  }

  override render() {
    const { children, fallback } = this.props;
    const { error, retrySerial } = this.state;
    const fetchKey = `${this.id}-${retrySerial}`;

    if (error) {
      if (fallback === undefined) {
        return <Retry retry={this._retry} message={error.message} />;
      } else if (typeof fallback === 'function') {
        return fallback({ error, retry: this._retry });
      }
      return fallback;
    }

    if (typeof children === 'function') {
      return children({ fetchKey, retry: this._retry });
    } else {
      return children;
    }
  }
}
